/**
 * Hair review r1, direction C (user pick 2026-10-11; docs/review/hair/2026-10-11-r1/README.md): two styles added beside
 * the drawn ones in hair.ts (same grid legend, same head template, `HAIR_STYLES` lists them).
 *
 * - `halfup` (woman): a tall wound bun on the crown, the rest of the hair gathered at the nape into ONE slender tail
 *   tied with a red silk bow whose two streamers hang beside it; the sides stop at the jaw and a single strand frames
 *   each cheek — the face stays open in every view and the back reads as a woman's (bun + bow + thin tail).
 * - `hightail` (man): all the hair combed straight back with the topknot's clean hairline (no locks by the face), tied
 *   high on the crown with a dark cord into a thick ponytail that arcs out behind the head and falls to the
 *   shoulder blades — higher tie, twice the thickness and a longer, wider swing than the woman's nape tail.
 *
 * The caps reuse the outlines of `highbun` (woman) and `topknot` (man) so they fit the head template exactly.
 * Lit from the upper right like every lit style: `H` sheen, `d` strand, `D` deep shadow, `r`/`R` ribbon and fold.
 */
import type { Grid, HairStyle } from './hair';

type Views = 'front' | 'front3' | 'side' | 'back3' | 'back';

const same = (ox: number, oy: number, rows: readonly string[], views: readonly Views[]): Partial<Record<Views, Grid>> =>
  Object.fromEntries(views.map((v) => [v, { ox, oy, rows }]));

// ── shared cap backs (the topknot / highbun outline: the whole skull covered from behind) ──

const CAP_SIDE: Grid = {
  ox: 9,
  oy: 10,
  rows: [
    '......DdhhhHh.....',
    '.....DdhhhhHHh....',
    '...DDdhhhhhhHHhh..',
    '..DDdhhdhhhhhHHhh.',
    '.DDdhhdhhhhdhhHHh.',
    'DDdhhdhhhhdhhhhHhh',
    'DDdhhdhhhhdhhhHh..',
    'DDdhhdhhhhdhhHh...',
    'DDdhhdhhhhdhHh....',
    'DDdhhdhhhhdh......',
    'DDdhhdhhh.........',
    'DDdhhdhh..........',
    'DDdhhd............',
    'DDdhhd............',
    '.Ddhhd............',
    '..Ddhd............',
    '...Ddh............',
    '....Dd............',
  ],
};

const CAP_BACK3: Grid = {
  ox: 9,
  oy: 10,
  rows: [
    '......Ddhhhh......',
    '.....Ddhhhhhh.....',
    '...DDdhhdhhHhh....',
    '..DDdhhhdhhHHhh...',
    '.DDdhhdhhdhhhHHh..',
    '.DDdhhdhhhdhhhHh..',
    'DDdhhdhhhhdhdhhHhh',
    'DDdhhdhhhhhdhdhhHh',
    'DDdhhdhhhhhdhdhhHh',
    'DDdhhdhhhhhdhhdhHh',
    'DDdhhdhhhhhdhhdhHh',
    'DDdhhdhhhhdhhhdhHh',
    '.DdhhdhhhhdhhhdhH.',
    '.Ddhhdhhhdhhhhdh..',
    '.DdhhdhhhdhhhdHh..',
    '..Ddhdhhdhhhhhh...',
    '...Dddhhdhhhh.....',
    '.....dhhhhd.......',
  ],
};

const CAP_BACK: Grid = {
  ox: 9,
  oy: 10,
  rows: [
    '......Ddhhhh......',
    '.....Ddhhhhhh.....',
    '...DDdhhdhhhhHh...',
    '..DDdhhdhhdhhHHh..',
    '.DDdhhdhhhdhhhHHh.',
    '.DDdhhdhhhhdhdhHh.',
    'DDdhhdhhhhhdhhdhHh',
    'DDdhdhhhhhhhdhdhHh',
    'DDdhdhhhhhhhdhhdHh',
    'DDdhdhhhhhhhdhhdHh',
    'DDdhhdhhhhhhdhhdHh',
    '.DdhhdhhhhhdhhdhH.',
    '.DdhhhdhhhhdhhdhH.',
    '.DdhhhdhhhdhhhdhH.',
    '.DdhhhhdhhdhhhdHh.',
    '..Ddhhhdhhdhhdhh..',
    '...Dddhhhhhhdhh...',
    '.....dhhhhhhd.....',
  ],
};

// ── woman: half-up, nape tail with a red bow ──

/** Parted in the middle, the sides falling to the jaw line (shorter than highbun's, no curtain). */
const HALFUP_FRONT: Grid = {
  ox: 9,
  oy: 10,
  rows: [
    '......dhhhHh......',
    '.....DdhhdHHh.....',
    '...DDdhhhdhHHhh...',
    '..DDdhhhhdhhHHhh..',
    '.DDdhhdhhdhhhhHhh.',
    '.DdhhdhhhdhhhdhHh.',
    'DDdhhdh....hdhhHhh',
    'DDdhd........hdhHh',
    'DDdh..........hhHh',
    'DDd............hhh',
    'Dd..............dh',
    'D................h',
  ],
};

const HALFUP_FRONT3: Grid = {
  ox: 9,
  oy: 10,
  rows: [
    '......DdhhHh......',
    '.....DdhhhhdH.....',
    '...DDdhhhhhdhHh...',
    '..DDdhhdhhhdhHHh..',
    '.DDdhhdhhhhdhhHHh.',
    '.DdhhdhhhhhdhhhHh.',
    'DDdhhdhhhd....dhHh',
    'DDdhhd.........hH.',
    'DDdhd.............',
    'DDdh..............',
    'DDd...............',
    'DD................',
    'D.................',
    'Dd................',
    '.Dd...............',
    '..Dd..............',
  ],
};

/** A tall wound bun: a coil line round it, a dark cord at its foot. */
const HALFUP_BUN = [
  '..dhhH..',
  '.dhhhHH.',
  'dhddhhHH',
  'dhhhddHh',
  'Dhhhhhdh',
  'DddhhhhH',
  '.Ddddhh.',
  '.DdddDD.',
];

/** The nape tail from behind: gathered hair, a red bow (two loops, the knot), two streamers, a slender tapering tail. */
const HALFUP_TAIL_BACK = [
  '...dhhH..',
  '...dhhH..',
  '.rr.R.rr.',
  'rRrrRrrRr',
  '.rR.R.Rr.',
  '..r.dhH.r',
  '..r.dhH.r',
  '..R.dhH.r',
  '.r..dhH..',
  '.r..dhH.R',
  '.R..dhH..',
  '....dhH..',
  '....dhH..',
  '.....dH..',
  '.....dH..',
  '.....d...',
];

/** The nape tail from the side: the bow's loop standing out behind, one streamer, the tail down the back. */
const HALFUP_TAIL_SIDE = [
  '....dhH',
  '.rr.dhH',
  'rRrRRhH',
  '.rR.dhH',
  '..r.dhH',
  '..r.dhH',
  '.r..dhH',
  '.R..dhH',
  '....dhH',
  '....dhH',
  '....dhH',
  '.....dH',
  '.....dH',
  '.....d.',
];

export const HALFUP: HairStyle = {
  id: 'halfup',
  label: 'ครึ่งมวยรวบหางต่ำ',
  sex: 'female',
  lit: true,
  cap: { front: HALFUP_FRONT, front3: HALFUP_FRONT3, side: CAP_SIDE, back3: CAP_BACK3, back: CAP_BACK },
  locks: {
    // One strand each side, from the parting to the jaw, just outside the cheek.
    front: {
      ox: 8,
      oy: 3,
      rows: [
        '....dh....Hh....',
        '...dh......hH...',
        '..dh........hH..',
        '.dh..........hH.',
        '.h............H.',
        '.h............H.',
        '.h............H.',
        '.d............H.',
        '..d..........d..',
      ],
    },
    // The near strand in front of the ear, the far one on the far cheek's edge.
    front3: {
      ox: 7,
      oy: 3,
      rows: [
        '..dh.........hH',
        '..h...........H',
        '..h...........H',
        '..h...........H',
        '..h...........H',
        '..h...........d',
        '..h............',
        '..d............',
        '...d...........',
      ],
    },
    // Profile: in front of the ear, ending at the jaw (user 10-11: longer, it hung down the neck as a dark bar).
    side: { ox: 0, oy: 0, rows: ['h.', 'h.', 'd.'] },
  },
  bun: same(4, 18, HALFUP_BUN, ['front', 'front3', 'side', 'back3', 'back']),
  tail: {
    front3: { ox: 12, oy: -4, rows: HALFUP_TAIL_SIDE },
    side: { ox: 12, oy: -4, rows: HALFUP_TAIL_SIDE },
    back3: { ox: 5, oy: -3, rows: HALFUP_TAIL_BACK },
    back: { ox: 4, oy: -3, rows: HALFUP_TAIL_BACK },
  },
  // The hairpin (ornaments.ts, anchored on the first bun) sits on the bun's flank so the flower shows against the
  // hair and its beads hang clear beside the head.
  anchors: {
    front: { crown: [-0.5, -11], buns: [[2.5, -14]] },
    front3: { crown: [-1.5, -11], buns: [[1.5, -14]] },
    side: { crown: [-1.5, -11], buns: [[1, -14]] },
    back3: { crown: [-1.5, -11], buns: [[-2.5, -14]] },
    back: { crown: [-0.5, -11], buns: [[-3, -14]] },
  },
};

// ── man: high ponytail ──

/** The topknot's cap: combed straight back, a clean square hairline over the brow, nothing by the face. */
const TOPKNOT_FRONT: Grid = {
  ox: 9,
  oy: 10,
  rows: [
    '......DhhhHh......',
    '....DDdhhhhHHh....',
    '...DDdhhdhhhHHh...',
    '..DDdhhdhhdhhHHh..',
    '.DDdhhdhhhdhhhHHh.',
    '.DDdhdhhhhhdhhhHh.',
    'DDdhd........dhHhh',
    'DDd............hHh',
    'DD..............hh',
    'DD..............hh',
    'D................h',
  ],
};

const TOPKNOT_FRONT3: Grid = {
  ox: 9,
  oy: 10,
  rows: [
    '......DhhhHh......',
    '....DDdhhhhHH.....',
    '...DDdhhdhhhHHh...',
    '..DDdhhdhhdhhHHh..',
    '.DDdhhdhhhdhhhHHh.',
    '.DdhhdhhhdhhhhhHh.',
    'DDdhhdhhd.....dhHh',
    'DDdhhdh.......dhH.',
    'DDdhhd............',
    'DDdhd.............',
    'DDdh..............',
    'DDd...............',
    'DD................',
    'Dd................',
    '.Dd...............',
    '..D...............',
  ],
};

/** The gathered root above the crown, bound with a dark cord. */
const HIGHTAIL_ROOT = ['.dhH.', 'dhhHH', 'DdddD'];

/** From behind: the cord at the crown, the thick tail lying over the back of the head and down to the shoulder blades. */
const HIGHTAIL_BACK = [
  '.DdddD.',
  '.dhhhH.',
  '.dhdhH.',
  '.dhdhH.',
  '.dhdhH.',
  '.dhdhH.',
  '.dhdhH.',
  '.dhdhH.',
  '..dhdhH',
  '..dhdhH',
  '..dhdhH',
  '..dhdhH',
  '..dhdhH',
  '..dhdhH',
  '..dhhhH',
  '..dhhhH',
  '.dhhdhH',
  '.dhhdhH',
  '.dhhdhH',
  '.dhhdhH',
  '.dhhdH.',
  '.dhhdH.',
  'dhhdH..',
  'dhhdH..',
  'dhhdH..',
  'dhdH...',
  'dhdH...',
  'dhH....',
  'dH.....',
  'd......',
];

/** From the side: tied on the crown, the tail arcs out behind the head and falls behind the back. */
const HIGHTAIL_SIDE = [
  '......dhH',
  '.....DDDd',
  '....dhhhH',
  '...dhhhH.',
  '..dhhhH..',
  '.dhhhH...',
  'dhhhH....',
  'dhhhH....',
  'dhdhH....',
  'dhdhH....',
  'dhdhH....',
  'dhdhH....',
  'dhdhH....',
  'dhdhH....',
  '.dhdhH...',
  '.dhdhH...',
  '.dhdhH...',
  '.dhdhH...',
  '.dhhhH...',
  '.dhhH....',
  '.dhhH....',
  '..dhH....',
  '..dhH....',
  '..dhH....',
  '..dH.....',
  '..d......',
];

export const HIGHTAIL: HairStyle = {
  id: 'hightail',
  label: 'หางม้าสูงรวบเกล้า',
  sex: 'male',
  lit: true,
  cap: { front: TOPKNOT_FRONT, front3: TOPKNOT_FRONT3, side: CAP_SIDE, back3: CAP_BACK3, back: CAP_BACK },
  bun: { ...same(2, 13, HIGHTAIL_ROOT, ['front', 'back']), ...same(3, 13, HIGHTAIL_ROOT, ['front3', 'side', 'back3']) },
  tail: {
    // Turned three-quarters the tail falls closer behind the head (less of it beside the face).
    front3: { ox: 9, oy: 11, rows: HIGHTAIL_SIDE },
    side: { ox: 11, oy: 11, rows: HIGHTAIL_SIDE },
    back3: { ox: 4, oy: 10, rows: HIGHTAIL_BACK },
    back: { ox: 3, oy: 10, rows: HIGHTAIL_BACK },
  },
  anchors: {
    front: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
    front3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
    side: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
    back3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
    back: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
  },
};

/** Direction C styles, listed after the drawn ones (hair.ts `HAIR_STYLES`). */
export const HAIR_C: readonly HairStyle[] = [HALFUP, HIGHTAIL];
